// Smart alignment guides: while dragging, objects snap to other
// objects' edges/centers and to the canvas edges/center, with magenta guide
// lines drawn on the overlay context.
const SNAP_THRESHOLD = 6; // logical px
const GUIDE_COLOR = "#ff2fbf";

// getTemplateSize: () => {width, height} of the design itself. Needed
// because the canvas DOM element's own size is now fixed to the available
// viewport space (independent of zoom/pan), so it can no longer be used to
// derive the template's logical dimensions the way canvas.getWidth()/zoom
// used to.
export function attachAlignmentGuides(canvas, getTemplateSize) {
  let vGuides = [];
  let hGuides = [];

  const collectCandidates = (movingObj) => {
    const { width: canvasW, height: canvasH } = getTemplateSize();
    const xs = [0, canvasW / 2, canvasW];
    const ys = [0, canvasH / 2, canvasH];
    const activeSet = new Set(canvas.getActiveObjects());
    canvas.getObjects().forEach((other) => {
      if (other === movingObj || activeSet.has(other) || !other.visible || other.excludeFromExport) return;
      const b = other.getBoundingRect(true, true);
      xs.push(b.left, b.left + b.width / 2, b.left + b.width);
      ys.push(b.top, b.top + b.height / 2, b.top + b.height);
    });
    return { xs, ys };
  };

  canvas.on("object:moving", (e) => {
    const obj = e.target;
    vGuides = [];
    hGuides = [];
    const { xs, ys } = collectCandidates(obj);
    const box = obj.getBoundingRect(true, true);
    const objXs = [box.left, box.left + box.width / 2, box.left + box.width];
    const objYs = [box.top, box.top + box.height / 2, box.top + box.height];

    let bestX = null;
    objXs.forEach((x) => {
      xs.forEach((cand) => {
        const diff = cand - x;
        if (Math.abs(diff) < SNAP_THRESHOLD && (!bestX || Math.abs(diff) < Math.abs(bestX.diff))) {
          bestX = { diff, cand };
        }
      });
    });
    let bestY = null;
    objYs.forEach((y) => {
      ys.forEach((cand) => {
        const diff = cand - y;
        if (Math.abs(diff) < SNAP_THRESHOLD && (!bestY || Math.abs(diff) < Math.abs(bestY.diff))) {
          bestY = { diff, cand };
        }
      });
    });

    if (bestX) {
      obj.left += bestX.diff;
      vGuides.push(bestX.cand);
    }
    if (bestY) {
      obj.top += bestY.diff;
      hGuides.push(bestY.cand);
    }
    if (bestX || bestY) obj.setCoords();
  });

  // Resize snapping: the edge being dragged by a scale handle snaps to the
  // same candidate lines. Skipped for rotated objects (edge math would lie).
  canvas.on("object:scaling", (e) => {
    const obj = e.target;
    vGuides = [];
    hGuides = [];
    if (Math.abs((obj.angle || 0) % 360) > 0.5) return;
    const corner = e.transform?.corner || "";
    if (!corner) return;

    const { xs, ys } = collectCandidates(obj);
    const box = obj.getBoundingRect(true, true);
    const findBest = (edge, candidates) => {
      let best = null;
      candidates.forEach((cand) => {
        const diff = cand - edge;
        if (Math.abs(diff) < SNAP_THRESHOLD && (!best || Math.abs(diff) < Math.abs(best.diff))) {
          best = { diff, cand };
        }
      });
      return best;
    };

    const movesLeft = corner.includes("l");
    const movesRight = corner.includes("r");
    const movesTop = corner.includes("t");
    const movesBottom = corner.includes("b");
    const isCorner = ["tl", "tr", "bl", "br"].includes(corner);

    const bestX = movesLeft
      ? findBest(box.left, xs)
      : movesRight
        ? findBest(box.left + box.width, xs)
        : null;
    const bestY = movesTop
      ? findBest(box.top, ys)
      : movesBottom
        ? findBest(box.top + box.height, ys)
        : null;

    const factorFromX = (best) => {
      const newWidth = movesLeft ? box.width - best.diff : box.width + best.diff;
      return newWidth > 4 ? newWidth / box.width : null;
    };
    const factorFromY = (best) => {
      const newHeight = movesTop ? box.height - best.diff : box.height + best.diff;
      return newHeight > 4 ? newHeight / box.height : null;
    };

    if (isCorner) {
      // Proportional corner drag: snap whichever axis is closer, scale both.
      const cx = bestX ? factorFromX(bestX) : null;
      const cy = bestY ? factorFromY(bestY) : null;
      let factor = null;
      if (cx && (!cy || Math.abs(bestX.diff) <= Math.abs(bestY.diff))) {
        factor = cx;
        vGuides.push(bestX.cand);
      } else if (cy) {
        factor = cy;
        hGuides.push(bestY.cand);
      }
      if (factor) {
        obj.scaleX *= factor;
        obj.scaleY *= factor;
        if (movesLeft) obj.left -= box.width * (factor - 1);
        if (movesTop) obj.top -= box.height * (factor - 1);
        obj.setCoords();
      }
    } else {
      if (bestX) {
        const factor = factorFromX(bestX);
        if (factor) {
          obj.scaleX *= factor;
          if (movesLeft) obj.left -= box.width * (factor - 1);
          vGuides.push(bestX.cand);
        }
      }
      if (bestY) {
        const factor = factorFromY(bestY);
        if (factor) {
          obj.scaleY *= factor;
          if (movesTop) obj.top -= box.height * (factor - 1);
          hGuides.push(bestY.cand);
        }
      }
      if (bestX || bestY) obj.setCoords();
    }
  });

  const clearGuides = () => {
    if (vGuides.length || hGuides.length) {
      vGuides = [];
      hGuides = [];
      canvas.requestRenderAll();
    }
  };
  canvas.on("mouse:up", clearGuides);

  canvas.on("before:render", () => {
    if (canvas.contextTop) canvas.clearContext(canvas.contextTop);
  });

  canvas.on("after:render", () => {
    if (!vGuides.length && !hGuides.length) return;
    const ctx = canvas.contextTop;
    if (!ctx) return;
    const zoom = canvas.getZoom() || 1;
    const vt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
    ctx.save();
    ctx.strokeStyle = GUIDE_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    vGuides.forEach((x) => {
      const sx = x * zoom + vt[4];
      ctx.beginPath();
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, canvas.getHeight());
      ctx.stroke();
    });
    hGuides.forEach((y) => {
      const sy = y * zoom + vt[5];
      ctx.beginPath();
      ctx.moveTo(0, sy);
      ctx.lineTo(canvas.getWidth(), sy);
      ctx.stroke();
    });
    ctx.restore();
  });
}
